/* eslint-disable @typescript-eslint/ban-ts-comment */
/* eslint-disable @typescript-eslint/no-explicit-any */
/**
 * Service for detecting barcodes using ZXing.js as a fallback for browsers
 * that don't support the native Barcode Detection API
 */

import QrScanner from 'qr-scanner';
import type { CameraSlotConfig } from './StorageService';

// Simple interface matching what BarcodeDetectionService expects
export interface BarcodeDetectionResult {
  code: string;
  format: string;
}

export default class ZXingBarcodeService {
  private zxingLoaded: boolean = false;
  private isLoading: boolean = false;
  private codeReader: any = null;
  private activeStreams: Map<
    string,
    { videoElement: HTMLVideoElement; active: boolean }
  > = new Map();
  private scanCallbacks: Map<
    string,
    (results: BarcodeDetectionResult[]) => void
  > = new Map();
  private streamSlotConfigs: Map<string, CameraSlotConfig> = new Map(); // Store slot config per stream
  private offscreenCanvas: HTMLCanvasElement;
  private lastScanTime: number = 0;
  private scanCooldown: number = 100; // ms between scan attempts
  private animationFrameId: number | null = null;
  private debugMode: boolean = false;

  // Image pool for memory efficiency
  private imagePool: HTMLImageElement[] = [];
  private readonly basePoolSize = 3;
  private readonly maxPoolSize = 6;
  private currentImageIndex = 0;
  private pendingDecodes = new Set<string>();

  // Per-stream canvas and processing optimization
  private streamCanvases: Map<string, HTMLCanvasElement> = new Map();
  private streamLastScan: Map<string, number> = new Map();
  private readonly maxCanvasSize = 1920; // Prevent memory issues

  // Sequential stream processing
  private streamProcessingOrder: string[] = []; // Array to maintain processing order
  private currentStreamIndex: number = 0; // Index of next stream to process

  // Fixed scales to try in order - no adaptive logic
  private readonly maxProcessingTime = 5000; // 5000ms max per camera per frame
  // Base scales configuration
  private readonly baseFixedScales = [1.0]; // Default scales

  // Global scale learning - shared across all cameras
  private globalSuccessfulScales: number[] = []; // Scales that worked for any camera
  private scaleSuccessCount: Map<number, number> = new Map(); // Track success frequency per scale
  private readonly maxTrackedScales = 5; // Limit tracked successful scales

  constructor(options?: { debug?: boolean }) {
    // Create main offscreen canvas
    this.offscreenCanvas = document.createElement("canvas");
    this.offscreenCanvas.width = 1280;
    this.offscreenCanvas.height = 720;

    // Pre-create image pool for memory efficiency
    this.initializeImagePool();

    this.debugMode = options?.debug || false;
    this.loadZXingLibrary();
  }

  /**
   * Initialize pool with dynamic expansion capability
   */
  private initializeImagePool(): void {
    for (let i = 0; i < this.basePoolSize; i++) {
      const img = new Image();
      // Pre-configure for optimal performance
      img.decoding = "sync";
      this.imagePool.push(img);
    }
    console.log(`Initialized image pool with ${this.basePoolSize} objects`);
  }

  /**
   * Dynamically expand image pool when needed
   */
  private expandImagePoolIfNeeded(): void {
    if (
      this.imagePool.length < this.maxPoolSize &&
      this.pendingDecodes.size > this.basePoolSize
    ) {
      const newImg = new Image();
      newImg.decoding = "sync";
      this.imagePool.push(newImg);
      console.log(`Expanded image pool to ${this.imagePool.length} objects`);
    }
  }

  /**
   * Get a reusable Image object from the pool
   */
  private getPooledImage(): HTMLImageElement {
    // Expand pool if needed for struggling cameras
    this.expandImagePoolIfNeeded();

    const img = this.imagePool[this.currentImageIndex];
    this.currentImageIndex =
      (this.currentImageIndex + 1) % this.imagePool.length;
    return img;
  }

  /**
   * Load the ZXing library dynamically
   */
  private loadZXingLibrary(): void {
    if (this.zxingLoaded || this.isLoading) return;

    this.isLoading = true;
    console.log("Loading ZXing.js library...");

    const script = document.createElement("script");
    script.src = "https://unpkg.com/@zxing/library@latest";
    script.async = true;

    script.onload = async () => {
      if (window.ZXing) {
        try {
          this.codeReader = new window.ZXing.BrowserMultiFormatReader();

          const supportedFormats = await this.getSupportedFormats();
          const zxingFormats = supportedFormats
            .map(fmt => window.ZXing.BarcodeFormat[fmt.toUpperCase()])
            .filter(Boolean); // skip unknowns

          const hints = new Map();
          hints.set(window.ZXing.DecodeHintType.POSSIBLE_FORMATS, zxingFormats);
          this.codeReader.reader.setHints(hints);

          console.log("ZXing reader initialized successfully");
          this.zxingLoaded = true;

          // Start frame processor if we have active streams
          if (this.activeStreams.size > 0) {
            this.startFrameProcessor();
          }
        } catch (error) {
          console.error("Failed to initialize ZXing reader:", error);
        }
      } else {
        console.error("ZXing loaded but not available in window object");
      }
      this.isLoading = false;
    };

    script.onerror = () => {
      console.error("Failed to load ZXing.js library");
      this.isLoading = false;
    };

    document.body.appendChild(script);
  }

  /**
   * Check if ZXing is ready to use
   */
  public isApiSupported(): boolean {
    // console.log(`Using fixed scales: ${this.baseFixedScales}`);
    return this.zxingLoaded && this.codeReader !== null;
  }

  /**
   * Check if ZXing is ready to use (alias for isApiSupported)
   */
  public isReady(): boolean {
    return this.zxingLoaded && this.codeReader !== null;
  }

  /**
   * Static method to check if ZXing can be loaded
   */
  public static isApiSupported(): boolean {
    return true; // We can always try to load ZXing
  }

  /**
   * Start continuous barcode scanning from a video element
   * This method never modifies the video stream to avoid blinking
   */
  public startContinuousScan(
    videoElement: HTMLVideoElement,
    onDetect: (results: BarcodeDetectionResult[]) => void,
    scanFrequency = 500, // ms (used to adjust scan cooldown)
    options?: {
      isExternalCamera?: boolean;
      debugElement?: HTMLElement;
      slotConfig?: CameraSlotConfig; // Per-slot enhancement configuration
    }
  ): string {
    // Generate unique ID for this stream
    const streamId = `stream_${Date.now()}`;

    // Adjust scan cooldown for this camera (external cameras might need different timing)
    if (options?.isExternalCamera) {
      this.scanCooldown = Math.min(scanFrequency, 300); // Faster scanning for external cameras
      console.log(
        `External camera detected, using faster scan rate: ${this.scanCooldown}ms`
      );
    } else {
      this.scanCooldown = scanFrequency;
    }

    // Add debug element if provided
    const debugOptions = options?.debugElement
      ? { debugElement: options.debugElement }
      : undefined;

    // Store the video element and callback
    this.activeStreams.set(streamId, {
      videoElement,
      active: true,
      // @ts-ignore
      isExternal: options?.isExternalCamera || false,
      debug: debugOptions,
    });

    this.scanCallbacks.set(streamId, onDetect);

    // Store slot config for this stream
    if (options?.slotConfig) {
      this.streamSlotConfigs.set(streamId, options.slotConfig);
    }

    // Add to processing order for sequential processing
    this.streamProcessingOrder.push(streamId);

    console.log(
      `Added stream ${streamId} for scanning ${options?.isExternalCamera ? "(external camera)" : ""
      }`
    );

    // Start the frame processor if ZXing is ready
    if (this.isApiSupported() && this.animationFrameId === null) {
      this.startFrameProcessor();
    }

    return streamId;
  }

  /**
   * Start the frame processor using requestAnimationFrame
   * This keeps processing running smoothly without blocking the UI
   */
  private startFrameProcessor(): void {
    if (this.animationFrameId !== null) return;

    console.log("Starting ZXing frame processor");

    const processFrames = async () => {
      // Process if enough time has passed since last scan
      const now = performance.now();
      if (now - this.lastScanTime >= this.scanCooldown) {
        this.lastScanTime = now;
        await this.processVideoFrames();
      }

      // Continue if we have active streams
      if (this.activeStreams.size > 0) {
        this.animationFrameId = requestAnimationFrame(processFrames);
      } else {
        this.animationFrameId = null;
      }
    };

    // Start the processing loop
    this.animationFrameId = requestAnimationFrame(processFrames);
  }

  /**
   * Get optimized scales based on global success data
   */
  private getOptimizedScales(): number[] {
    // Start with globally successful scales (most successful first)
    const prioritizedScales = [...this.globalSuccessfulScales];

    // Add base scales that aren't already in the prioritized list
    const remainingScales = this.baseFixedScales.filter(
      (scale) => !prioritizedScales.includes(scale)
    );

    // Combine: successful scales first, then remaining base scales
    const finalScales = [...prioritizedScales, ...remainingScales];

    if (this.debugMode) {
      console.log("Using optimized scales:", finalScales);
    }

    return finalScales;
  }

  /**
   * Update global successful scales when a detection succeeds
   */
  private updateGlobalSuccessfulScales(successfulScale: number): void {
    // Increment success count for this scale
    const currentCount = this.scaleSuccessCount.get(successfulScale) || 0;
    this.scaleSuccessCount.set(successfulScale, currentCount + 1);

    // Add to global successful scales if not already there
    if (!this.globalSuccessfulScales.includes(successfulScale)) {
      this.globalSuccessfulScales.unshift(successfulScale);

      // Limit the number of tracked scales
      if (this.globalSuccessfulScales.length > this.maxTrackedScales) {
        const removedScale = this.globalSuccessfulScales.pop();
        if (removedScale !== undefined) {
          this.scaleSuccessCount.delete(removedScale);
        }
      }
    } else {
      // Re-sort based on success count (most successful first)
      this.globalSuccessfulScales.sort((a, b) => {
        const countA = this.scaleSuccessCount.get(a) || 0;
        const countB = this.scaleSuccessCount.get(b) || 0;
        return countB - countA; // Descending order
      });
    }

    if (this.debugMode) {
      console.log(
        `Updated global successful scales: ${this.globalSuccessfulScales
        }, scale ${successfulScale} success count: ${this.scaleSuccessCount.get(
          successfulScale
        )}`
      );
    }
  }

  /**
   * Handle successful barcode detection result
   */
  private handleDetectionResult(
    result: any,
    streamId: string,
    scale: number,
    callback: (results: BarcodeDetectionResult[]) => void
  ): void {
    // Update global successful scales for other cameras to learn from
    this.updateGlobalSuccessfulScales(scale);

    callback([
      {
        code: result.data,
        format: result.format
      },
    ]);

    this.pendingDecodes.delete(streamId);
  }

  /**
   * Get or create dedicated canvas for a stream
   */
  private getStreamCanvas(
    streamId: string,
    width: number,
    height: number
  ): HTMLCanvasElement {
    let canvas = this.streamCanvases.get(streamId);

    if (!canvas) {
      canvas = document.createElement("canvas");
      this.streamCanvases.set(streamId, canvas);
    }

    // Resize only if needed
    if (canvas.width !== width || canvas.height !== height) {
      canvas.width = width;
      canvas.height = height;
    }

    return canvas;
  }

  /**
   * Simple multi-scale detection with global scale learning
   */
  private async detectBarcodeMultiScale(
    videoElement: HTMLVideoElement,
    streamId: string,
    callback: (results: BarcodeDetectionResult[]) => void
  ): Promise<void> {
    if (this.pendingDecodes.has(streamId)) {
      return;
    }

    this.pendingDecodes.add(streamId);
    const startTime = performance.now();

    try {
      // Get optimized scales based on global success data
      const scales = this.getOptimizedScales();

      // Try each scale in order
      for (const scale of scales) {
        // Stop if taking too long
        if (performance.now() - startTime > this.maxProcessingTime) {
          if (this.debugMode) {
            console.log(
              `Stream ${streamId} hit time budget, stopping at scale ${scale}`
            );
          }
          break;
        }

        const result = await this.detectAtScale(
          videoElement,
          streamId,
          scale
        );

        if (result) {
          this.handleDetectionResult(result, streamId, scale, callback);
          return;
        }
      }

      // No detection found after trying all scales
      if (this.debugMode) {
        console.debug(
          `No barcode detected in stream ${streamId} after trying all scales`
        );
      }
    } catch (error) {
      if (this.debugMode) {
        console.debug(`Multi-scale detection failed for ${streamId}:`, error);
      }
    } finally {
      this.pendingDecodes.delete(streamId);
    }
  }

  /**
   * Detect barcode at a specific scale
   */
  private async detectAtScale(
    videoElement: HTMLVideoElement,
    streamId: string,
    scale: number
  ): Promise<any> {
    // Calculate optimal canvas size
    const targetWidth = Math.min(
      videoElement.videoWidth * scale,
      this.maxCanvasSize
    );
    const targetHeight = Math.min(
      videoElement.videoHeight * scale,
      this.maxCanvasSize
    );

    // Maintain aspect ratio
    const aspectRatio = videoElement.videoWidth / videoElement.videoHeight;
    let canvasWidth = targetWidth;
    let canvasHeight = targetHeight;

    if (targetWidth / aspectRatio > targetHeight) {
      canvasWidth = targetHeight * aspectRatio;
    } else {
      canvasHeight = targetWidth / aspectRatio;
    }

    // Get dedicated canvas for this stream
    const canvas = this.getStreamCanvas(
      streamId,
      Math.round(canvasWidth),
      Math.round(canvasHeight)
    );
    const ctx = canvas.getContext("2d", {
      willReadFrequently: true,
      alpha: false,
      desynchronized: true,
    });

    if (!ctx) return null;

    // Simple image smoothing logic
    if (scale >= 1.0) {
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = "high";
    } else {
      ctx.imageSmoothingEnabled = false;
    }

    // Minimal padding only for very small scales
    const padding =
      scale <= 0.3 ? Math.min(canvas.width, canvas.height) * 0.03 : 0;
    const drawWidth = canvas.width - padding * 2;
    const drawHeight = canvas.height - padding * 2;

    ctx.drawImage(
      videoElement,
      0,
      0,
      videoElement.videoWidth,
      videoElement.videoHeight,
      padding,
      padding,
      drawWidth,
      drawHeight
    );

    // Apply enhancement using slot config
    const slotConfig = this.streamSlotConfigs.get(streamId);
    if (slotConfig && slotConfig.enhancementEnabled) {
      this.enhanceImageForBarcodeDetection(ctx, canvas, slotConfig);
    }

    const timeout = 800;

    return new Promise((resolve) => {
      const timeoutId = setTimeout(() => resolve(null), timeout);

      try {
        const img = this.getPooledImage();

        const onLoad = async () => {
          try {
            let resultZXIng = null;
            try {
              resultZXIng = await this.codeReader.decode(img);
            } catch {
              resultZXIng = null;
            }

            if (resultZXIng && resultZXIng.text) {
              resolve({ data: resultZXIng.text, format: resultZXIng.format ?? "unknown" });
              return;
            }

            let resultQRDedicated = null;
            try {
              resultQRDedicated = await QrScanner.scanImage(img, { returnDetailedScanResult: true });
            } catch {
              resultQRDedicated = null;
            }

            if (resultQRDedicated && resultQRDedicated.data) {
              resolve({ data: resultQRDedicated.data, format: 11 });
            } else {
              // Both scanners failed to detect anything
              resolve(null);
            }
          } catch (e) {
            console.log("Error during detect at scale", e)

            clearTimeout(timeoutId);
            resolve(null);
          } finally {
            img.onload = null;
            img.onerror = null;
          }
        };

        const onError = () => {
          clearTimeout(timeoutId);
          resolve(null);
          img.onload = null;
          img.onerror = null;
        };

        img.onload = onLoad;
        img.onerror = onError;
        img.src = canvas.toDataURL("image/jpeg", 0.95);
      } catch  {
        clearTimeout(timeoutId);
        resolve(null);
      }
    });
  }

  /**
   * Process frames from all active video streams
   */
  private async processVideoFrames(): Promise<void> {
    if (!this.isApiSupported() || this.streamProcessingOrder.length === 0) return;

    const now = performance.now();

    // Process one stream at a time in round-robin fashion
    // Always advance to the next stream, even if current one is processed
    const streamId = this.streamProcessingOrder[this.currentStreamIndex];
    const stream = this.activeStreams.get(streamId);

    // Advance to next stream for next frame
    this.currentStreamIndex = (this.currentStreamIndex + 1) % this.streamProcessingOrder.length;

    // Check if current stream exists and is active
    if (!stream || !stream.active) {
      return; // Skip this frame, next stream will be tried next frame
    }

    // Individual cooldown per stream
    const lastScan = this.streamLastScan.get(streamId) || 0;
    // @ts-ignore
    const streamCooldown = stream.isExternal ? 300 : this.scanCooldown;

    if (now - lastScan < streamCooldown) {
      return; // Skip this frame, next stream will be tried next frame
    }

    const videoElement = stream.videoElement;

    // Skip if video is not ready
    if (
      videoElement.readyState < 2 ||
      videoElement.videoWidth <= 0 ||
      videoElement.videoHeight <= 0
    ) {
      return; // Skip this frame, next stream will be tried next frame
    }

    // Update last scan time for this stream
    this.streamLastScan.set(streamId, now);

    // Get callback for this stream
    const callback = this.scanCallbacks.get(streamId);
    if (!callback) {
      return; // Skip this frame, next stream will be tried next frame
    }

    try {
      // Use simple multi-scale detection and wait for completion
      await this.detectBarcodeMultiScale(
        videoElement,
        streamId,
        callback
      );

      // Display debug frame if requested
      // @ts-ignore
      if (stream.debug?.debugElement && this.debugMode) {
        const canvas =
          this.streamCanvases.get(streamId) || this.offscreenCanvas;
        // @ts-ignore
        this.showDebugFrame(stream.debug.debugElement, canvas);
      }
    } catch (error) {
      console.error(`Error in stream ${streamId}:`, error);
      this.pendingDecodes.delete(streamId);
    }
  }

  /**
   * Apply image enhancements to improve barcode detection
   * Uses slot-specific enhancement configuration
   */
  private enhanceImageForBarcodeDetection(
    ctx: CanvasRenderingContext2D,
    canvas: HTMLCanvasElement | undefined,
    slotConfig: CameraSlotConfig
  ): void {
    try {
      // Use the provided canvas or fall back to offscreen canvas
      const targetCanvas = canvas || this.offscreenCanvas;

      // Get the image data
      const imageData = ctx.getImageData(
        0,
        0,
        targetCanvas.width,
        targetCanvas.height
      );

      const data = imageData.data;

      // Use slot-specific enhancement settings
      const contrast = slotConfig.contrast || 1;
      const brightness = slotConfig.brightness || 0;

      for (let i = 0; i < data.length; i += 4) {
        // Apply contrast and brightness adjustment
        data[i] = Math.min(
          255,
          Math.max(0, (data[i] - 128) * contrast + 128 + brightness)
        );
        data[i + 1] = Math.min(
          255,
          Math.max(0, (data[i + 1] - 128) * contrast + 128 + brightness)
        );
        data[i + 2] = Math.min(
          255,
          Math.max(0, (data[i + 2] - 128) * contrast + 128 + brightness)
        );
        // Alpha channel remains unchanged (data[i+3])
      }

      // Zoom the image using slot-specific zoom factor
      const zoomFactor = slotConfig.zoom || 1;
      if (zoomFactor !== 1 && targetCanvas.width > 0 && targetCanvas.height > 0) {
        const tempCanvas = document.createElement('canvas');
        tempCanvas.width = targetCanvas.width;
        tempCanvas.height = targetCanvas.height;
        const tempCtx = tempCanvas.getContext('2d');
        if (tempCtx) {
          tempCtx.putImageData(imageData, 0, 0);

          const srcW = targetCanvas.width / zoomFactor;
          const srcH = targetCanvas.height / zoomFactor;
          const srcX = (targetCanvas.width - srcW) / 2;
          const srcY = (targetCanvas.height - srcH) / 2;

          // Redraw the zoomed region onto the original canvas
          ctx.clearRect(0, 0, targetCanvas.width, targetCanvas.height);
          ctx.drawImage(
            tempCanvas,
            srcX,
            srcY,
            srcW,
            srcH,
            0,
            0,
            targetCanvas.width,
            targetCanvas.height
          );
          return;
        }
      }

      // Put the modified image data back
      ctx.putImageData(imageData, 0, 0);
    } catch (error) {
      console.error("Error enhancing image:", error);
    }
  }

  /**
   * Show debug frame to help diagnose camera issues
   */
  private showDebugFrame(
    debugElement: HTMLElement,
    canvas: HTMLCanvasElement
  ): void {
    try {
      // Create a thumbnail of the current frame
      const img = document.createElement("img");
      img.src = canvas.toDataURL("image/jpeg", 0.5); // Use JPEG for smaller size
      img.width = 200;
      img.style.border = "1px solid #ccc";
      img.style.marginTop = "5px";

      // Clear previous content and add new image
      debugElement.innerHTML = "";
      debugElement.appendChild(img);
    } catch (error) {
      console.error("Error showing debug frame:", error);
    }
  }

  /**
   * Enable or disable debug mode
   */
  public setDebugMode(enabled: boolean): void {
    this.debugMode = enabled;
    console.log(`ZXing debug mode ${enabled ? "enabled" : "disabled"}`);
  }

  /**
   * Stop continuous scanning for a specific stream ID
   * @param streamId The ID of the stream to stop
   */
  public stopContinuousScanById(streamId: string): void {
    if (this.activeStreams.has(streamId)) {
      console.log(`Stopping ZXing scanning for stream ${streamId}`);
      this.activeStreams.delete(streamId);
      this.scanCallbacks.delete(streamId);
      this.streamSlotConfigs.delete(streamId); // Clean up slot config

      // Remove from processing order
      const orderIndex = this.streamProcessingOrder.indexOf(streamId);
      if (orderIndex !== -1) {
        this.streamProcessingOrder.splice(orderIndex, 1);
        // Adjust current index if necessary
        if (this.currentStreamIndex >= orderIndex && this.currentStreamIndex > 0) {
          this.currentStreamIndex--;
        }
        // Wrap around if we're at the end
        if (this.currentStreamIndex >= this.streamProcessingOrder.length && this.streamProcessingOrder.length > 0) {
          this.currentStreamIndex = 0;
        }
      }

      if (this.activeStreams.size === 0 && this.animationFrameId !== null) {
        cancelAnimationFrame(this.animationFrameId);
        this.animationFrameId = null;
        // Reset processing state when no streams
        this.streamProcessingOrder = [];
        this.currentStreamIndex = 0;
      }
    }
  }

  /**
   * Stop continuous scanning for all streams
   */
  public stopContinuousScan(): void {
    console.log("Stopping all ZXing scanning");

    // Clear all active streams
    this.activeStreams.clear();
    this.scanCallbacks.clear();
    this.streamSlotConfigs.clear(); // Clear slot configs
    this.pendingDecodes.clear();
    this.streamLastScan.clear();

    // Reset processing state
    this.streamProcessingOrder = [];
    this.currentStreamIndex = 0;

    // Cancel the animation frame
    if (this.animationFrameId !== null) {
      cancelAnimationFrame(this.animationFrameId);
      this.animationFrameId = null;
    }

    // Reset the reader if available
    if (this.isReady() && this.codeReader) {
      try {
        this.codeReader.reset();
      } catch (error) {
        console.error("Error resetting ZXing reader:", error);
      }
    }

    // Clean up resources
    this.cleanupImagePool();
    this.cleanupStreamCanvases();

    // Optional: Reset global scale learning (uncomment if you want fresh start each session)
    // this.globalSuccessfulScales = [];
    // this.scaleSuccessCount.clear();
  }

  /**
   * Clean up the image pool to free memory
   */
  private cleanupImagePool(): void {
    this.imagePool.forEach((img) => {
      img.onload = null;
      img.onerror = null;
      img.src = ""; // Clear the src to free memory
    });
    console.log("Cleaned up image pool");
  }

  /**
   * Clean up per-stream canvases
   */
  private cleanupStreamCanvases(): void {
    this.streamCanvases.forEach((canvas) => {
      const ctx = canvas.getContext("2d");
      if (ctx) {
        ctx.clearRect(0, 0, canvas.width, canvas.height);
      }
    });
    this.streamCanvases.clear();
    console.log("Cleaned up stream canvases");
  }

  /**
   * Get supported formats
   */
  public getSupportedFormats(): Promise<string[]> {
    return Promise.resolve([
      "qr_code",
      "code_128",
      "code_39",
      "ean_13",
      "ean_8",
      "upc_a",
      "upc_e",
      "data_matrix",
    ]);
  }

  /**
   * Get current global successful scales (for debugging/monitoring)
   */
  public getGlobalSuccessfulScales(): number[] {
    return [...this.globalSuccessfulScales];
  }

  /**
   * Reset global scale learning (useful for testing different scenarios)
   */
  public resetGlobalScaleLearning(): void {
    this.globalSuccessfulScales = [];
    this.scaleSuccessCount.clear();
    console.log("Reset global scale learning");
  }
}

// Add ZXing to window object type
declare global {
  interface Window {
    ZXing: any;
  }
}
